import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIVE, followJob, get, type Job, type LimitSpec, type Me, type Registry } from "./api";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => get<Me>("/me"), refetchInterval: 60_000 });
}

export function useRegistry() {
  return useQuery({ queryKey: ["registry"], queryFn: () => get<Registry>("/registry"), staleTime: Infinity });
}

/** limit key -> spec */
export function useSpecs(): Record<string, LimitSpec> {
  const { data } = useRegistry();
  return useMemo(() => Object.fromEntries((data?.limits ?? []).map((s) => [s.key, s])), [data]);
}

/** A job, kept up to date while it is queued or running (live events, with polling as a fallback). */
export function useLiveJob(jobId: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => get<Job>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ACTIVE.includes(q.state.data.status) ? 5000 : false),
  });
  const [live, setLive] = useState<Job | null>(null);
  const status = query.data?.status;

  useEffect(() => {
    if (!jobId || !status || !ACTIVE.includes(status)) return;
    const ctrl = new AbortController();
    let previewFetched = false;
    followJob(
      jobId,
      (j) => {
        setLive(j);
        // a quick preview has been stored: fetch it so the plots appear while the full map runs
        if (j.message?.startsWith("full map") && !previewFetched) {
          previewFetched = true;
          qc.invalidateQueries({ queryKey: ["job", jobId] });
        }
        if (!ACTIVE.includes(j.status)) {
          qc.invalidateQueries({ queryKey: ["job", jobId] });
          qc.invalidateQueries({ queryKey: ["jobs"] });
          qc.invalidateQueries({ queryKey: ["me"] });
          qc.invalidateQueries({ queryKey: ["project"] });
        }
      },
      ctrl.signal,
    ).catch(() => {
      /* polling continues */
    });
    return () => ctrl.abort();
  }, [jobId, status, qc]);

  const job = query.data && live && ACTIVE.includes(query.data.status) ? { ...query.data, ...live } : query.data;
  return { ...query, job };
}
